import { useCallback } from 'react';
import { useMooConfig } from '../../provider';
import { useMessageSources } from './sources-context';
import { SourceTooltip } from './SourceTooltip';
import styles from './SourcesCitation.module.css';

export interface SourcesCitationProps {
  readonly citationIndex: number;
}

/** Inline `[N]` chip rendered in place of the citation token. Clicking it
 *  forwards to the host's `onSourceClick` handler. When the matching
 *  source is missing (server reference but no entry in `meta.sources`),
 *  the chip renders disabled so the number is still visible. */
export function SourcesCitation({ citationIndex }: SourcesCitationProps) {
  const sources = useMessageSources();
  const { onSourceClick } = useMooConfig();
  const source = sources.find((s) => s.citationIndex === citationIndex);

  const handleClick = useCallback(() => {
    if (source && onSourceClick) onSourceClick(source);
  }, [source, onSourceClick]);

  if (!source) {
    return (
      <span
        className={styles.missingCitation}
        title="Source not available"
        data-testid="citation-missing"
      >
        {citationIndex}
      </span>
    );
  }

  return (
    <SourceTooltip source={source}>
      <button
        type="button"
        className={`${styles.citationBadge} ${
          source.type === 'url' ? styles.webBadge : ''
        }`}
        data-source-type={source.type}
        onClick={handleClick}
        aria-label={`Open source ${citationIndex}: ${source.title}`}
        data-testid="citation-chip"
        data-print-visible
      >
        {citationIndex}
      </button>
    </SourceTooltip>
  );
}
